import { useCallback, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type FormItem } from "@njbsoft/form-logic";
import { useFormSubmission, type FormSubmissionAttachmentMeta } from "@/hooks/use-form-submissions";
import { useTemplateVersion } from "@/hooks/use-form-template";
import { useDeviceInstallationDetail } from "@/hooks/use-device-installations";
import { SubmissionChangeRequests } from "@/components/change-requests/submission-change-requests";
import { alwaysReviewedRider } from "@/components/form-submissions/always-reviewed-rider";
import { InspectionNextSteps } from "@/components/form-submissions/inspection-next-steps";
import { PerformerStateCard } from "@/components/form-submissions/performer-state-card";
import { RequirementsTable } from "@/components/form-submissions/requirements-table";
import { ReviewActionCard } from "@/components/form-submissions/review-action-card";
import { type SubmissionReviewSlots } from "@/components/form-submissions/review-slots";
import { SubmissionIdentityCard } from "@/components/form-submissions/submission-identity-card";
import { ResolveIncidentCta } from "@/components/form-submissions/resolve-incident-cta";
import { StaleTargetBanner } from "@/components/form-submissions/stale-target-banner";
import { SupersedesChain } from "@/components/form-submissions/supersedes-chain";
import { AttachmentGallery } from "@/components/form-submissions/attachment-gallery";
import { InspectionSendLetterButton } from "@/components/notices/inspection-send-letter-button";
import { SubmissionLettersCard } from "@/components/notices/submission-letters-card";
import { SubmissionReportFormsCard } from "@/components/report-forms/submission-report-forms-card";
import { SubmissionViolationProposals } from "@/components/violations/submission-violation-proposals";
import { SubmissionViolationsCard } from "@/components/violations/submission-violations-card";
import { FormRenderer, FormLetterhead, parseFormDefinition } from "@njbsoft/form-renderer";
import { renderFacilityRefSlot } from "@/components/form-renderer/facility-ref-slot";
import { resolveFormTheme } from "@njbsoft/form-ui";
import { useOrgLogoObjectUrl } from "@/hooks/use-org-logo";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import {
  Breadcrumbs,
  Button,
  Card,
  EmptyValue,
  PageHeader,
  PageSkeleton,
  Skeleton,
} from "@/components/ui";

export const Route = createFileRoute("/_authenticated/test-activity/submissions/$id/")({
  component: SubmissionDetailPage,
});

/**
 * Submission detail — the regulatory auditor surface per AWWA M14 §10. The
 * left column carries the identity card (`SubmissionIdentityCard`: template +
 * pinned version, assignment / facility / installation drill-ins, the
 * tester's device-of-record answer, a row per rider, the field-clock
 * performed_at vs server-clock received_at pair — a date answer prints as the
 * day the performer named, `performed_date`), the performer state card (the
 * credential / gauge facts with the acceptance-requirements evaluation table
 * beneath them), the review state card with the review decision verbs, an
 * accepted submission's next steps, and the supersession chain. The right
 * column renders the verbatim response document READ-ONLY through the shared
 * FormRenderer (the auditor sees exactly what the tester saw) with per-item
 * evidence galleries injected via `attachmentSlot`, an accepted submission's
 * violations and letters beneath it with its report forms between them (a
 * superseded submission keeps the report forms card alone), plus the GPS
 * capture block when the promoted gps columns are present. A submission
 * carrying riders renders through the host that decides them: a
 * change-request set through `SubmissionChangeRequests` (which decides an
 * inspection's violation proposals beside it), violation proposals alone
 * through `SubmissionViolationProposals`. The host adds the riders' sections
 * above the grid, one identity row per rider and the review card's verbs.
 * The header's actions carry Print, and beside it Send letter on an accepted
 * inspection for a caller who may read letters and send them.
 */
function SubmissionDetailPage() {
  const { t, i18n } = useTranslation(["form-submissions", "common", "form-renderer", "nav"]);
  const { id } = Route.useParams();

  const activeOrgName = useAppStore((s) => s.activeOrgName);
  const { objectUrl: orgLogoUrl } = useOrgLogoObjectUrl();
  const { can } = usePermissions();
  const canMessageTester = can("messages", "read");
  const canReadLetters = can("notices", "read");

  const { data: submission, isLoading, isError, refetch } = useFormSubmission(id);
  const versionQuery = useTemplateVersion(submission?.template_id, submission?.template_version_id);
  const installationQuery = useDeviceInstallationDetail(submission?.device_installation_id ?? "");

  const attachmentsByLinkId = useMemo(() => {
    const map = new Map<string, FormSubmissionAttachmentMeta[]>();
    for (const att of submission?.attachments ?? []) {
      const bucket = map.get(att.link_id);
      if (bucket) {
        bucket.push(att);
      } else {
        map.set(att.link_id, [att]);
      }
    }
    return map;
  }, [submission?.attachments]);

  const attachmentSlot = useCallback(
    (item: FormItem) => (
      <AttachmentGallery attachments={attachmentsByLinkId.get(item.link_id) ?? []} />
    ),
    [attachmentsByLinkId],
  );

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !submission) {
    return (
      <div className="p-8">
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("errors.loadDetailFailed")}
        </div>
        <div className="mt-4">
          <Link
            to="/test-activity"
            search={{ tab: "submissions" }}
            className="text-sm text-primary hover:underline"
          >
            {t("common:back")}
          </Link>
        </div>
      </div>
    );
  }

  const language: "en" | "es" = i18n.language.startsWith("es") ? "es" : "en";

  // The wire definition is an opaque JSON object; published versions passed
  // the strict publish gates, but guard on `sections` before handing the
  // document to the renderer.
  const definition = versionQuery.data
    ? parseFormDefinition(versionQuery.data.definition)
    : undefined;
  const renderable = definition !== undefined && Array.isArray(definition.sections);

  const templateTitle = submission.template_title;

  const hasGps = typeof submission.gps_lat === "number" && typeof submission.gps_lng === "number";
  const setId = submission.change_request_set_id;
  const proposing = submission.violation_proposals.length > 0;
  const accepted = submission.status === "accepted";

  // The page, with what a submission's riders add to it when it carries them.
  const page = (riders?: SubmissionReviewSlots) => {
    const canReview = can("form_submissions", "review") && (riders?.canDecide ?? true);
    return (
      // This is the designed print surface, so the page gutter collapses for
      // print: on paper it would sit INSIDE the stylesheet's `@page` margin and
      // push the document a further 2rem in on every edge of every sheet.
      <div className="p-8 print:p-0">
        <div className="print:hidden">
          <Breadcrumbs
            items={[
              {
                label: t("testActivity.submissions", { ns: "nav" }),
                to: "/test-activity",
                search: { tab: "submissions" },
              },
              { label: t("detail.title") },
            ]}
          />
          <PageHeader
            title={t("detail.title")}
            actions={
              <>
                <Button variant="secondary" onClick={() => window.print()}>
                  {t("detail.print")}
                </Button>
                {accepted && canReadLetters && (
                  <InspectionSendLetterButton submission={submission} />
                )}
              </>
            }
          />
        </div>
        {riders && <div className="mt-6">{riders.section}</div>}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12 print:mt-0 print:block">
          <div className="space-y-6 lg:col-span-5 print:hidden">
            <SubmissionIdentityCard
              submission={submission}
              templateVersion={versionQuery.data?.version}
              installationSerial={installationQuery.data?.serial_number}
              riders={riders}
            />

            {/* The evaluation table rides the performer card's region: the
                three performer facts and the WHY behind the routing on one
                card, beneath the credential and gauge blocks. */}
            <PerformerStateCard submission={submission}>
              <RequirementsTable
                evaluation={submission.requirements_evaluation ?? null}
                rider={alwaysReviewedRider(submission)}
              />
            </PerformerStateCard>

            <ReviewActionCard
              submission={submission}
              canReview={canReview}
              canMessage={canMessageTester}
              riders={riders?.card}
            />

            <ResolveIncidentCta submission={submission} />

            {accepted && <InspectionNextSteps submission={submission} />}

            <SupersedesChain submission={submission} />
          </div>

          <div className="space-y-6 lg:col-span-7">
            {submission.target_stale && (
              <div className="print:hidden space-y-6">
                <StaleTargetBanner reason={submission.target_stale_reason} />
              </div>
            )}

            <div
              className="form-doc overflow-clip rounded-xl border border-[var(--form-card-border)] shadow-sm print:rounded-none print:border-0 print:shadow-none"
              data-form-theme={resolveFormTheme(definition)}
            >
              <FormLetterhead
                orgName={activeOrgName ?? ""}
                programLine={t("form-renderer:letterhead.programLine")}
                logoUrl={orgLogoUrl}
                formTitle={templateTitle ?? t("detail.documentTitle")}
                metaLine={
                  versionQuery.data
                    ? `${t("detail.version")} ${String(versionQuery.data.version)}`
                    : undefined
                }
              />
              <div className="bg-[var(--form-canvas)] p-4 sm:p-6">
                {versionQuery.isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-5 w-3/4" />
                  </div>
                ) : renderable && definition ? (
                  <FormRenderer
                    definition={definition}
                    answers={submission.responses}
                    language={language}
                    attachmentSlot={attachmentSlot}
                    facilityRefSlot={renderFacilityRefSlot}
                  />
                ) : (
                  <p className="text-sm text-muted">{t("detail.documentUnavailable")}</p>
                )}
              </div>
            </div>

            {/* The case, report form and letter tables need the wide column:
                in the narrow one their last columns scroll out of sight at
                laptop widths. A superseded test keeps its report forms for the
                record; cases and letters belong to the accepted test alone. */}
            {(accepted || submission.status === "superseded") && (
              <div className="space-y-6 print:hidden">
                {accepted && <SubmissionViolationsCard submission={submission} />}
                <SubmissionReportFormsCard submission={submission} />
                {accepted && canReadLetters && <SubmissionLettersCard submission={submission} />}
              </div>
            )}

            {hasGps && (
              <div className="print:hidden">
                <Card title={t("detail.gps.title")}>
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                    <div>
                      <dt className="text-muted">{t("detail.gps.lat")}</dt>
                      <dd className="mt-0.5 font-mono text-on-surface">
                        {submission.gps_lat?.toFixed(6)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted">{t("detail.gps.lng")}</dt>
                      <dd className="mt-0.5 font-mono text-on-surface">
                        {submission.gps_lng?.toFixed(6)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted">{t("detail.gps.accuracy")}</dt>
                      <dd className="mt-0.5 font-mono text-on-surface">
                        {typeof submission.gps_accuracy_m === "number" ? (
                          submission.gps_accuracy_m.toFixed(1)
                        ) : (
                          <EmptyValue />
                        )}
                      </dd>
                    </div>
                  </dl>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (setId) {
    return (
      <SubmissionChangeRequests
        submission={submission}
        setId={setId}
        onRefetch={() => void refetch()}
      >
        {page}
      </SubmissionChangeRequests>
    );
  }
  if (proposing) {
    return (
      <SubmissionViolationProposals submission={submission} onRefetch={() => void refetch()}>
        {page}
      </SubmissionViolationProposals>
    );
  }
  return page();
}
